import {Dayjs} from "dayjs";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";

const MINUTES_PER_DAY = 24 * 60;

/** The days of a sheet, 1 = Monday … 7 = Sunday. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * What a screen knows of the pharmacy hours: still loading, failed to load,
 * never filled (see `isOpeningFilled`), or filled.
 */
export type OpeningState = 'loading' | 'failed' | 'missing' | 'filled';

/** Minutes of the day, wall clock, end excluded. */
export interface MinuteRange {
    start: number;
    end: number;
}

/**
 * Minutes since midnight of a stored bound ("09:00" or "09:00:00"), null
 * when the bound is blank: the sheet keeps an empty string or null for a
 * half-day the pharmacy did not fill.
 */
const boundToMinutes = (bound: string | null | undefined): number | null => {
    if (!bound) {
        return null;
    }

    const [hours, minutes] = bound.split(':').map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
    }

    return hours * 60 + minutes;
};

/** ISO weekday of a day, 1 = Monday … 7 = Sunday, as the sheet numbers its days. */
const isoWeekdayOf = (day: Dayjs): number => day.day() === 0 ? 7 : day.day();

/** Open ranges of a weekday of the sheet, by the rules `openRangesOn` describes. */
const openRangesOnWeekday = (opening: HealthcenterOpening, weekday: number): MinuteRange[] => {
    const sheet = opening.openings.find((entry) => entry.day === weekday);
    const opensMorning = boundToMinutes(sheet?.hours.morning.opening);
    const closesMorning = boundToMinutes(sheet?.hours.morning.closing);
    const opensAfternoon = boundToMinutes(sheet?.hours.afternoon.opening);
    const closesAfternoon = boundToMinutes(sheet?.hours.afternoon.closing);

    const candidates: [number | null, number | null][] = closesMorning === null && opensAfternoon === null
        ? [[opensMorning, closesAfternoon]]
        : [[opensMorning, closesMorning], [opensAfternoon, closesAfternoon]];

    return candidates
        .filter((range): range is [number, number] => range[0] !== null && range[1] !== null && range[0] < range[1])
        .map(([start, end]) => ({start, end}))
        .sort((left, right) => left.start - right.start);
};

/**
 * The ranges the pharmacy is open on a day, read exactly as the booking
 * server reads the same sheet, so the grey of the agenda and the slots a
 * patient may book through a link never disagree:
 *
 * - the morning opening and the afternoon closing alone describe a day open
 *   without a break, as Connect enters it;
 * - otherwise each half-day counts only with both of its bounds;
 * - a day without any range is a closing day.
 *
 * Null when the pharmacy has no sheet at all: nothing is known, so nothing
 * is closed. A reversed range describes nothing and is skipped.
 */
export const openRangesOn = (opening: HealthcenterOpening | null | undefined, day: Dayjs): MinuteRange[] | null => {
    if (!opening) {
        return null;
    }

    return openRangesOnWeekday(opening, isoWeekdayOf(day));
};

/**
 * Whether the pharmacy filled its hours: a sheet opening at least one day of
 * the week. A sheet saved with every day closed tells nothing either, as no
 * pharmacy is closed all week.
 */
export const isOpeningFilled = (opening: HealthcenterOpening | null | undefined): boolean => {
    if (!opening) {
        return false;
    }

    return ISO_WEEKDAYS.some((weekday) => openRangesOnWeekday(opening, weekday).length > 0);
};

/**
 * The ranges the pharmacy is closed on a day: the complement of its open
 * ranges over the whole day. Empty when the pharmacy has no sheet — nothing
 * to grey — and the whole day when its sheet says it is closed that day.
 */
export const closedRangesOn = (opening: HealthcenterOpening | null | undefined, day: Dayjs): MinuteRange[] => {
    const open = openRangesOn(opening, day);

    if (open === null) {
        return [];
    }

    const closed: MinuteRange[] = [];
    let cursor = 0;

    open.forEach((range) => {
        if (range.start > cursor) {
            closed.push({start: cursor, end: range.start});
        }

        cursor = Math.max(cursor, range.end);
    });

    if (cursor < MINUTES_PER_DAY) {
        closed.push({start: cursor, end: MINUTES_PER_DAY});
    }

    return closed;
};

/**
 * Whether the pharmacy is closed at a moment, by its sheet. False without a
 * sheet, as nothing is then known to be closed.
 */
export const isClosedAt = (opening: HealthcenterOpening | null | undefined, moment: Dayjs): boolean => {
    const minute = moment.hour() * 60 + moment.minute();

    return closedRangesOn(opening, moment).some((range) => range.start <= minute && minute < range.end);
};
